import { RentingEndpoint, ReviewEnpoint } from "../../../api";
import ManagementEndpoint from "../../../api/management";
import { getBaseUrl } from "../../../api/baseApi";
import { useState, useEffect } from "react";
import {
    Table,
    Modal,
    Button,
    Image,
    Tag,
    Form,
    Input,
    message,
    Tabs,
    Card,
    Row,
    Col,
    Statistic,
} from "antd";
import {
    EyeOutlined,
    DollarOutlined,
    CheckOutlined,
    CreditCardOutlined,
} from "@ant-design/icons";

const BASE_URL = getBaseUrl();


function RentProperty({ userData }) {

    const [transactionData, setTransactionData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [photoModalVisible, setPhotoModalVisible] = useState(false);
    const [depositModalVisible, setDepositModalVisible] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [selectedParentTransaction, setSelectedParentTransaction] = useState(null);
    const [invoiceUtilities, setInvocieUtilities] = useState([]);
    const [utilitiesModalVisible, setUtilitiesModalVisible] = useState(false);
    const [selectedPropertyData, setSelectedPropertyData] = useState(null);
    const [utilityInvoices, setUtilityInvoices] = useState([]);
    const [rentRequest, setRentRequest] = useState(null);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [selectedPaymentData, setSelectedPaymentData] = useState(null);
    const [form] = Form.useForm();



    const withBaseUrl = (photo) => {
        if (!photo) return [];

        try {
            const parsed = JSON.parse(photo);

            // Jika hasil parse adalah array, tambahkan prefix BASE_URL ke semua
            if (Array.isArray(parsed)) {
                return parsed.map(item => BASE_URL + item);
            }
        } catch (err) {
            // Jika bukan array → anggap string single image
            return [BASE_URL + photo];
        }

        return [];
    };

    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [selectedReviewProperty, setSelectedReviewProperty] = useState(null);

    const [extendModalVisible, setExtendModalVisible] = useState(false);
    const [extendLoading, setExtendLoading] = useState(false);
    const [extendAmount, setExtendAmount] = useState("");
    const [selectedExtendProperty, setSelectedExtendProperty] = useState(null);

    const management = new ManagementEndpoint();

    // Fungsi untuk membuka modal review
    const showReviewModal = (record) => {
        setSelectedReviewProperty(record);
        setRating(0);
        setComment("");
        setReviewModalVisible(true);
    };

    // Fungsi submit review
    const handleSubmitReview = async () => {
        if (rating === 0) {
            message.warning("Silakan pilih rating sebelum submit");
            return;
        }

        try {
            setReviewLoading(true);
            const reviewEndpoint = new ReviewEnpoint();
            await reviewEndpoint.create({
                property_id: selectedReviewProperty.property_id,
                rating,
                comment,
                transaction_id: selectedReviewProperty.id,
            });
            message.success("Ulasan berhasil ditambahkan!");
            setReviewModalVisible(false);
        } catch (err) {
            console.error(err);
            message.error("Gagal menambahkan ulasan");
        } finally {
            setReviewLoading(false);
        }
    };

    // Fungsi untuk membuka modal extend
    const showExtendModal = (record) => {
        setSelectedExtendProperty(record);
        setExtendAmount("");
        setExtendModalVisible(true);
    };

    // Fungsi submit extend
    const handleSubmitExtend = async () => {
        if (!extendAmount || parseFloat(extendAmount) <= 0) {
            message.warning("Silakan masukkan jumlah sewa yang valid");
            return;
        }



        try {
            setExtendLoading(true);
            const rentingEndpoint = new RentingEndpoint();
            const reqData = {
                property_id: selectedExtendProperty.property.id,
                quantity: extendAmount
            }

            await rentingEndpoint.extendRenting(reqData);
            message.success("Permintaan perpanjangan berhasil dikirim!");
            setExtendModalVisible(false);
        } catch (err) {
            console.error(err);
            message.error("Gagal memperpanjang sewa");
        } finally {
            setExtendLoading(false);
        }
    };

    // Handle pay utility invoice
    const handlePayUtilityInvoice = async (record) => {
        try {
            await management.verifyUtilityInvoice(record.id, record.transaction_type);
            message.success("Utility invoice paid successfully!");

            // Refresh utility invoices
            const rentingEndpoint = new RentingEndpoint();
            const refreshedInvoiceData = await rentingEndpoint.getUtilitiesInvoice();
            const flattenedRefreshedInvoices = [
                ...(refreshedInvoiceData?.waterInvocie || []),
                ...(refreshedInvoiceData?.electricityInvocie || []),
            ];
            setInvocieUtilities(flattenedRefreshedInvoices);
            const filteredInvoices = flattenedRefreshedInvoices.filter(
                (invoice) => invoice.property_id === selectedPropertyData.property_id
            );
            setUtilityInvoices(filteredInvoices);
        } catch (error) {
            console.error("Error paying utility invoice:", error);
            message.error("Failed to pay utility invoice");
        }
    };

    useEffect(() => {
        const fetchTransactionData = async () => {
            try {
                const rentingEndpoint = new RentingEndpoint();
                const response = await rentingEndpoint.getRenting();

                const invoiceData = await rentingEndpoint.getUtilitiesInvoice();
                const rentData = await rentingEndpoint.getRequest();
                setRentRequest(rentData.sent_request_invocie)
                // Flatten utility invoices into single array
                const flattenedInvoices = [
                    ...(invoiceData?.waterInvocie || []),
                    ...(invoiceData?.electricityInvocie || []),
                ];
                setInvocieUtilities(flattenedInvoices);
                // Use sent_rent_request data as provided by user
                const transactions = response.sent_rent_request || [];

                setTransactionData(transactions);
            } catch (err) {
                console.error("Error fetching transactions:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchTransactionData();
    }, [userData.id]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <h6>Loading your properties...</h6>
            </div>
        );
    }

    // Calculate summary statistics based on status
    const totalRequests = rentRequest?.length || 0;
    const unpaidCount = transactionData.filter(item => item.status === 'Pending').length;
    const paidCount = transactionData.filter(item => item.status === 'Verified').length;
    const checkInCount = transactionData.filter(item => item.status === 'Check In').length;
    const checkOutCount = transactionData.filter(item => item.status === 'Check Out' || item.status === 'Cancelled and Refunded').length;

    // Filter data for each tab based on status
    const requestRentData = rentRequest || [];
    const unpaidRentData = transactionData.filter(item => item.status === 'Pending');
    const paidRentData = transactionData.filter(item => item.status === 'Verified');
    const checkInData = transactionData.filter(item => item.status === 'Check In');
    const checkOutData = transactionData.filter(item => item.status === 'Check Out' || item.status === 'Cancelled and Refunded');

    // Jika tidak ada transaction
    if (!loading && transactionData.length === 0 && totalRequests === 0) {
        return (
            <div className="ltn__myaccount-tab-content-inner">
                <p className="text-center py-5">You have no rental transactions yet.</p>
            </div>
        );
    }



    // Format date helper
    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

       const ts = Number(timestamp); // <--- WAJIB

       return new Date(ts).toLocaleDateString("id-ID", {
           year: "numeric",
           month: "long",
           day: "numeric",
       });
   };


    // Format currency helper
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
        }).format(amount);
    };

    const withdrawRequest = async (id) => {
        try {
            setReviewLoading(true);
            const rentEndp = new RentingEndpoint();
            await rentEndp.deleteRequest(id);
            const rentData = await rentEndp.getRequest();
            setRentRequest(rentData.sent_request_invocie)
            message.success("Request sewa dibatalkan");
        } catch (err) {
            console.error(err);
            message.error("Gagal membatalkan request sewa");
        } finally {
            setReviewLoading(false);
        }
    }

    // Handle payment modal
    const showPaymentModal = (record) => {
        setSelectedPaymentData(record);
        setPaymentModalVisible(true);
    };

    const handlePayRent = async (id) => {
        try {
            setReviewLoading(true);
            // Here you would integrate with your payment gateway
            const rentingEndpoint = new RentingEndpoint();
            await rentingEndpoint.payRent(id)
            // For now, we'll simulate a successful payment
            message.success("Payment successful! Your rent has been paid.");
            setPaymentModalVisible(false);

            // Refresh data after payment
            const response = await rentingEndpoint.getRenting();
            const transactions = response.sent_rent_request || [];
            setTransactionData(transactions);

        } catch (err) {
            console.error(err);
            message.error("Payment failed. Please try again.");
        } finally {
            setReviewLoading(false);
        }
    };

    const handleCheckInOut = async (id, status) => {
        try {
            setReviewLoading(true);
            const rentingEndpoint = new RentingEndpoint();
            await rentingEndpoint.checkInOut(id, status);

            message.success(`${status} successful!`);

            // Refresh data after check in/out
            const response = await rentingEndpoint.getRenting();
            const transactions = response.sent_rent_request || [];
            setTransactionData(transactions);

        } catch (err) {
            console.error(err);
            message.error(`${status} failed. Please try again.`);
        } finally {
            setReviewLoading(false);
        }
    };

    // Handle photo modal
    const showPhotoModal = (photo) => {
        setSelectedPhoto(photo);
        setPhotoModalVisible(true);
        setUtilitiesModalVisible(false);
    };

    // Handle deposit modal
    const showDepositModal = (parentTransaction) => {
        setSelectedParentTransaction(parentTransaction);
        setDepositModalVisible(true);
    };

    // Handle utilities modal
    const showUtilitiesModal = (record) => {
        setSelectedPropertyData(record);
        // Filter utility invoices by property_id
        const filteredInvoices = invoiceUtilities.filter(
            (invoice) => invoice.property_id === record.property_id
        );
        setUtilityInvoices(filteredInvoices);
        setUtilitiesModalVisible(true);
    };


    console.log(requestRentData);


    return (
        <div>
            <div className="ltn__myaccount-tab-content-inner">
                {/* Summary Cards */}
                <Row gutter={8} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                        <Card size="small">
                            <Statistic
                                title="Request Rent"
                                value={totalRequests}
                                valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                        <Card size="small">
                            <Statistic
                                title="Unpaid Rent"
                                value={unpaidCount}
                                valueStyle={{ color: '#faad14', fontSize: '16px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                        <Card size="small">
                            <Statistic
                                title="Paid Rent"
                                value={paidCount}
                                valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                        <Card size="small">
                            <Statistic
                                title="Check In"
                                value={checkInCount}
                                valueStyle={{ color: '#722ed1', fontSize: '16px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                        <Card size="small">
                            <Statistic
                                title="Check Out"
                                value={checkOutCount}
                                valueStyle={{ color: '#13c2c2', fontSize: '16px' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Tabs */}
                <Tabs defaultActiveKey="1" type="card">
                    <Tabs.TabPane tab={`Request Rent (${totalRequests})`} key="1">
                        <p>Jika request anda di setujui anda akan melihat sewa anda di unpaid rent</p>
                        <Table
                            columns={[
                                {
                                    title: "Property",
                                    dataIndex: "property",
                                    key: "property",
                                    render: (property) => property?.name || "N/A",
                                },
                                {
                                    title: "Request Date",
                                    dataIndex: "transaction_date",
                                    key: "transaction_date",
                                    render: (transaction_date) => formatDate(transaction_date),
                                },
                                {
                                    title: "Rent Quantity",
                                    key: "nominal",
                                    render: (_, data) => `${Number(data.nominal)} ${data.rent_type}`,
                                },
                                {
                                    title: "Status",
                                    dataIndex: "status",
                                    key: "status",
                                    render: (status) => (
                                        <Tag color={status === 'Pending' ? 'orange' : 'green'}>
                                            {status?.toUpperCase() || 'PENDING'}
                                        </Tag>
                                    ),
                                },
                                {
                                    title: "Action",
                                    key: "id",
                                    dataIndex: "id",

                                    render: (id) => (
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => withdrawRequest(id)}
                                        >
                                            Withdraw
                                        </Button>
                                    ),
                                },
                            ]}
                            dataSource={requestRentData}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} requests`,
                            }}
                        />
                    </Tabs.TabPane>

                    <Tabs.TabPane tab={`Unpaid Rent (${unpaidCount})`} key="2">
                        <Table
                            columns={[
                                {
                                    title: "Property Information",
                                    dataIndex: "property",
                                    key: "property",
                                    render: (property) => (
                                        <div>
                                            <div style={{ fontWeight: "bold" }}>{property?.name}</div>
                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                {property?.full_address}, {property?.city}, {property?.province}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    title: "Rent",
                                    dataIndex: "nominal",
                                    key: "nominal",
                                    render: (amount) => formatCurrency(amount),
                                },
                                {
                                    title: "Deposit",
                                    dataIndex: "parentTransaction",
                                    key: "parentTransaction",
                                    render: (deposit) => formatCurrency(deposit.nominal),
                                },
                                {
                                    title: "Total Payable",
                                    key: "totalPayable",
                                    render: (text, record) => {
                                        const rentAmount = parseFloat(record.nominal || 0);
                                        const depositAmount = parseFloat(record.parentTransaction?.nominal || 0);
                                        return formatCurrency(rentAmount + depositAmount);
                                    },
                                },

                                {
                                    title: "Action",
                                    key: "id",
                                    dataIndex: "id",

                                    render: (text, record) => (
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => showPaymentModal(record)}
                                        >
                                            Pay Invoice
                                        </Button>
                                    ),
                                },

                            ]}
                            dataSource={unpaidRentData}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} unpaid rents`,
                            }}
                        />
                    </Tabs.TabPane>

                    <Tabs.TabPane tab={`Paid Rent (${paidCount})`} key="3">
                        <Table
                            columns={[
                                {
                                    title: "Property Information",
                                    dataIndex: "property",
                                    key: "property",
                                    render: (property) => (
                                        <div>
                                            <div style={{ fontWeight: "bold" }}>{property?.name}</div>
                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                {property?.full_address}, {property?.city}, {property?.province}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    title: "Amount",
                                    dataIndex: "nominal",
                                    key: "nominal",
                                    render: (amount) => formatCurrency(amount),
                                },
                                {
                                    title: "Payment Date",
                                    dataIndex: "transaction_date",
                                    key: "payment_date",
                                    render: (date) => formatDate(date),
                                },
                                {
                                    title: "Action",
                                    key: "id",
                                    dataIndex: "id",

                                    render: (text, record) => (
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => handleCheckInOut(record.id, "Check In")}
                                            loading={reviewLoading}
                                        >
                                            Check In
                                        </Button>
                                    ),
                                },
                            ]}
                            dataSource={paidRentData}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} paid rents`,
                            }}
                        />
                    </Tabs.TabPane>

                    <Tabs.TabPane tab={`Check In (${checkInCount})`} key="4">
                        <Table
                            columns={[
                                {
                                    title: "Property Information",
                                    dataIndex: "property",
                                    key: "property",
                                    render: (property) => (
                                        <div>
                                            <div style={{ fontWeight: "bold" }}>{property?.name}</div>
                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                <i className="icon-placeholder" /> {property?.full_address},{" "}
                                                {property?.city}, {property?.province}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    title: "Rental Information",
                                    key: "rental",
                                    render: (record) => (
                                        <div>
                                            <div>
                                                <Tag color="blue">{record.transaction_type?.toUpperCase()}</Tag>
                                            </div>
                                            <div style={{ fontWeight: "bold", color: "#52c41a" }}>
                                                {formatCurrency(record.nominal)}
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                Transaction: {formatDate(record.transaction_date)}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    title: "Lease Expiration",
                                    dataIndex: "expired_date",
                                    key: "expired_date",
                                    render: (expired_date) => {
                                        return (
                                            <div>
                                                {expired_date ? (
                                                    <div style={{ color: expired_date < Date.now() ? "#ff4d4f" : "#52c41a" }}>
                                                        {formatDate(expired_date)}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: "#999" }}>No expiration</span>
                                                )}
                                            </div>
                                        )

                                    },
                                },
                                {
                                    title: "Status",
                                    dataIndex: "status",
                                    key: "status",
                                    render: (status, record) => {

                                        const statusConfig = {
                                            'Pending': { color: 'orange', text: 'Pending' },
                                            'Verified': { color: 'green', text: 'Verified' },
                                            'Check In': { color: 'purple', text: 'Checked In' },
                                            'Check Out': { color: 'cyan', text: 'Checked Out' },
                                            'Cancelled and Refunded': { color: 'red', text: 'Deposit Refunded' },
                                        };
                                        const config = statusConfig[status] || statusConfig['Pending'];

                                        return <Tag color={config.color}>{config.text}</Tag>;
                                    },
                                },

                                {
                                    title: "Deposit Check",
                                    key: "deposit",
                                    render: (record) =>
                                        record.parentTransaction ? (
                                            <Button
                                                type="primary"
                                                icon={<DollarOutlined />}
                                                size="small"
                                                onClick={() => showDepositModal(record.parentTransaction)}
                                            >
                                                View Deposit
                                            </Button>
                                        ) : (
                                            <span style={{ color: "#999" }}>No deposit</span>
                                        ),
                                },
                                {
                                    title: "Utilities",
                                    key: "utilities",
                                    render: (record) => (
                                        <Button
                                            type="default"
                                            icon={<CheckOutlined />}
                                            size="small"
                                            onClick={() => showUtilitiesModal(record)}
                                        >
                                            Check
                                        </Button>
                                    ),
                                },
                                {
                                    title: "Action",
                                    key: "id",
                                    dataIndex: "id",

                                    render: (text, record) => (
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => handleCheckInOut(record.id, "Check Out")}
                                            loading={reviewLoading}
                                        >
                                            Check Out
                                        </Button>
                                    ),
                                },
                            ]}
                            dataSource={checkInData}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} check-ins`,
                            }}
                        />
                    </Tabs.TabPane>

                    <Tabs.TabPane tab={`Check Out (${checkOutCount})`} key="5">
                        <Table
                            columns={[
                                {
                                    title: "Property Information",
                                    dataIndex: "property",
                                    key: "property",
                                    render: (property) => (
                                        <div>
                                            <div style={{ fontWeight: "bold" }}>{property?.name}</div>
                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                <i className="icon-placeholder" /> {property?.full_address},{" "}
                                                {property?.city}, {property?.province}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    title: "Rental Information",
                                    key: "rental",
                                    render: (record) => (
                                        <div>
                                            <div>
                                                <Tag color="blue">{record.transaction_type?.toUpperCase()}</Tag>
                                            </div>
                                            <div style={{ fontWeight: "bold", color: "#52c41a" }}>
                                                {formatCurrency(record.nominal)}
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                Transaction: {formatDate(record.transaction_date)}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    title: "Lease Expiration",
                                    dataIndex: "expired_date",
                                    key: "expired_date",
                                    render: (expired_date) => {
                                        return (
                                            <div>
                                                {expired_date ? (
                                                    <div style={{ color: expired_date < Date.now() ? "#ff4d4f" : "#52c41a" }}>
                                                        {formatDate(expired_date)}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: "#999" }}>No expiration</span>
                                                )}
                                            </div>
                                        )

                                    },
                                },
                                {
                                    title: "Status",
                                    dataIndex: "status",
                                    key: "status",
                                    render: (status, record) => {

                                        const statusConfig = {
                                            'Pending': { color: 'orange', text: 'Pending' },
                                            'Verified': { color: 'green', text: 'Verified' },
                                            'Check In': { color: 'purple', text: 'Checked In' },
                                            'Check Out': { color: 'cyan', text: 'Checked Out' },
                                            'Cancelled and Refunded': { color: 'red', text: 'Deposit Refunded' },
                                        };
                                        const config = statusConfig[status] || statusConfig['Pending'];

                                        return <Tag color={config.color}>{config.text}</Tag>;
                                    },
                                },

                                {
                                    title: "Deposit Check",
                                    key: "deposit",
                                    render: (record) =>
                                        record.parentTransaction ? (
                                            <Button
                                                type="primary"
                                                icon={<DollarOutlined />}
                                                size="small"
                                                onClick={() => showDepositModal(record.parentTransaction)}
                                            >
                                                View Deposit
                                            </Button>
                                        ) : (
                                            <span style={{ color: "#999" }}>No deposit</span>
                                        ),
                                },

                                {
                                    title: "Action",
                                    key: "action",
                                    render: (record) => (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <Button
                                                type="primary"
                                                size="small"
                                                onClick={() => showReviewModal(record)}
                                            >
                                                Leave Review
                                            </Button>
                                            <Button
                                                type="default"
                                                size="small"
                                                onClick={() => showExtendModal(record)}
                                            >
                                                Extend Property
                                            </Button>
                                        </div>
                                    ),
                                },
                            ]}
                            dataSource={checkOutData}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} check-outs`,
                            }}
                        />
                    </Tabs.TabPane>
                </Tabs>



                {/* Deposit Modal */}
                <Modal
                    title="Deposit Transaction Details"
                    open={depositModalVisible}
                    onCancel={() => setDepositModalVisible(false)}
                    footer={null}
                    width={600}
                >
                    {selectedParentTransaction && (
                        <div>
                            <div style={{ marginBottom: 16 }}>
                                <h5>Deposit Information</h5>
                            </div>

                            <div style={{ display: "grid", gap: "12px" }}>
                                <div>
                                    <strong>Transaction ID:</strong> {selectedParentTransaction.id}
                                </div>
                                <div>
                                    <strong>Type:</strong>{" "}
                                    <Tag color="green">
                                        {selectedParentTransaction.transaction_type?.toUpperCase()}
                                    </Tag>
                                </div>
                                <div>
                                    <strong>Amount:</strong>{" "}
                                    <span style={{ fontWeight: "bold", color: "#52c41a" }}>
                                        {formatCurrency(selectedParentTransaction.nominal)}
                                    </span>
                                </div>
                                <div>
                                    <strong>Transaction Date:</strong>{" "}
                                    {formatDate(selectedParentTransaction.transaction_date)}
                                </div>
                                <div>
                                    <strong>Expiration:</strong>{" "}
                                    {selectedParentTransaction.expired_date
                                        ? formatDate(selectedParentTransaction.expired_date)
                                        : "No expiration"}
                                </div>
                                <div>
                                    <strong>Status:</strong>{" "}
                                    {(() => {
                                        const status = selectedParentTransaction.status;
                                        const statusConfig = {
                                            'Pending': { color: 'orange', text: 'Pending' },
                                            'Verified': { color: 'green', text: 'Verified' },
                                            'Cancelled and Refunded': { color: 'blue', text: 'Refunded' },
                                        };
                                        const config = statusConfig[status] || statusConfig['Pending'];

                                        return <Tag color={config.color}>{config.text}</Tag>;
                                    })()}
                                </div>
                                {selectedParentTransaction.photo && (
                                    <div>
                                        <strong>Proof Photo:</strong>
                                        <div style={{ marginTop: 8 }}>
                                            <Image
                                                src={selectedParentTransaction.photo}
                                                alt="Deposit proof"
                                                style={{ maxWidth: "100%", maxHeight: "200px" }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div >
                        </div >
                    )
                    }
                </Modal >

                {/* Utility Invoice Modal */}
                < Modal
                    title="Utility Invoice"
                    open={utilitiesModalVisible}
                    onCancel={() => {
                        setUtilitiesModalVisible(false);
                        form.resetFields();
                    }}
                    footer={null}
                    width={800}
                >
                    <div>
                        {/* Property Information */}
                        {selectedPropertyData && (
                            <div
                                style={{
                                    marginBottom: 20,
                                    padding: 16,
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: 8,
                                }}
                            >
                                <h5>Property: {selectedPropertyData.property?.name}</h5>
                                <p style={{ margin: 0, color: "#666" }}>
                                    {selectedPropertyData.property?.full_address},{" "}
                                    {selectedPropertyData.property?.city},{" "}
                                    {selectedPropertyData.property?.province}
                                </p>
                            </div>
                        )}

                        {/* Utility Invoices Table */}
                        < div >
                            <h5 style={{ marginBottom: 16 }}>Utility Transactions</h5>
                            <Table
                                columns={[
                                    {
                                        title: "Type",
                                        dataIndex: "transaction_type",
                                        key: "type",
                                        render: (type) => (
                                            <Tag color={type === "water" ? "blue" : "yellow"}>
                                                {type?.toUpperCase()}
                                            </Tag>
                                        ),
                                    },
                                    {
                                        title: "Amount",
                                        dataIndex: "nominal",
                                        key: "amount",
                                        render: (amount) => (
                                            <span style={{ fontWeight: "bold", color: "#52c41a" }}>
                                                {formatCurrency(amount)}
                                            </span>
                                        ),
                                    },
                                    {
                                        title: "Date",
                                        dataIndex: "transaction_date",
                                        key: "date",
                                        render: (date) => formatDate(date),
                                    },
                                    {
                                        title: "Status",
                                        dataIndex: "status",
                                        key: "status",
                                        render: (status) => {
                                            const statusConfig = {
                                                'Pending': { color: 'orange', text: 'Pending' },
                                                'Verified': { color: 'green', text: 'Verified' },
                                                'Cancelled': { color: 'red', text: 'Cancelled' },
                                                'Refunded': { color: 'blue', text: 'Refunded' }
                                            };
                                            const config = statusConfig[status] || statusConfig['Pending'];
                                            return <Tag color={config.color}>{config.text}</Tag>;
                                        },
                                    },
                                    {
                                        title: "Photo",
                                        dataIndex: "photo",
                                        key: "photo",
                                        render: (photo) => {
                                            if (!photo) {
                                                return <span style={{ color: "#999" }}>No photo</span>;
                                            }

                                            // withBaseUrl always returns an array now
                                            const photoUrls = withBaseUrl(photo);
                                            const photoCount = photoUrls.length;

                                            return (
                                                <Button
                                                    type="link"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => showPhotoModal(photoUrls)}
                                                >
                                                    View {photoCount > 1 ? `(${photoCount})` : ''}
                                                </Button>
                                            );
                                        },
                                    },
                                    {
                                        title: "Pay",
                                        key: "pay",
                                        render: (_, record) => (
                                            record.status === 'Pending' ? (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<CreditCardOutlined />}
                                                    onClick={() => handlePayUtilityInvoice(record)}
                                                >
                                                    Pay
                                                </Button>
                                            ) : (
                                                <span style={{ color: "#52c41a" }}>Paid</span>
                                            )
                                        ),
                                    },
                                ]}
                                dataSource={utilityInvoices}
                                rowKey="id"
                                size="small"
                                pagination={{
                                    pageSize: 5,
                                    showSizeChanger: false,
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} of ${total} invoices`,
                                }}
                                locale={{
                                    emptyText: "No utility invoices found",
                                }}
                            />
                        </div >
                    </div >
                </Modal >

                {/* Review Modal */}
                < Modal
                    title={`Tambahkan Ulasan: ${selectedReviewProperty?.property?.name}`}
                    open={reviewModalVisible}
                    onCancel={() => setReviewModalVisible(false)}
                    onOk={handleSubmitReview}
                    okText="Submit"
                    confirmLoading={reviewLoading}
                >
                    <div className="add-a-review">
                        <h6>Rating Anda:</h6>
                        <div className="product-ratting">
                            <ul style={{ display: "flex", gap: 4, padding: 0, listStyle: "none" }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <li key={star}>
                                        <i
                                            className={
                                                star <= rating ? "fas fa-star" : "far fa-star"
                                            }
                                            style={{
                                                cursor: "pointer",
                                                color: "#fadb14",
                                                fontSize: 20,
                                            }}
                                            onClick={() => setRating(star)}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <Form layout="vertical" style={{ marginTop: 16 }}>
                        <Form.Item label="Komentar Anda">
                            <Input.TextArea
                                rows={4}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Tulis komentar Anda..."
                            />
                        </Form.Item>
                    </Form>
                </Modal >

                {/* Extend Property Modal */}
                <Modal
                    title={`Perpanjang Sewa: ${selectedExtendProperty?.property?.name}`}
                    open={extendModalVisible}
                    onCancel={() => setExtendModalVisible(false)}
                    onOk={handleSubmitExtend}
                    okText="Submit"
                    confirmLoading={extendLoading}
                >
                    <div style={{ padding: '16px 0' }}>
                        <Form layout="vertical">
                            <Form.Item
                                label="Jumlah Sewa Baru"
                                required
                                help="Masukkan jumlah sewa untuk perpanjangan properti"
                            >
                                <Input
                                    placeholder="Masukkan jumlah sewa"
                                    type="number"
                                    value={extendAmount}
                                    onChange={(e) => setExtendAmount(e.target.value)}
                                />
                            </Form.Item>
                        </Form>
                    </div>
                </Modal>

                {/* Payment Gateway Modal */}
                <Modal
                    title={
                        <div style={{
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#1890ff'
                        }}>
                            🏦 Payment Gateway
                        </div>
                    }
                    open={paymentModalVisible}
                    onCancel={() => setPaymentModalVisible(false)}
                    footer={null}
                    width={600}
                    centered
                >
                    {selectedPaymentData && (
                        <div style={{ padding: '20px 0' }}>
                            {/* Property Info */}
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                padding: '20px',
                                borderRadius: '12px',
                                marginBottom: '24px',
                                textAlign: 'center'
                            }}>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>
                                    {selectedPaymentData.property?.name}
                                </h3>
                                <p style={{ margin: 0, opacity: 0.9, fontSize: '14px', color: "white" }}>
                                    {selectedPaymentData.property?.city}, {selectedPaymentData.property?.province}
                                </p>
                            </div>
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                marginBottom: '24px'
                            }}>
                                <h4 style={{ margin: '0 0 16px 0', color: '#333' }}>Rent Details</h4>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    padding: '8px 0',
                                    borderBottom: '1px solid #e9ecef'
                                }}>
                                    <span style={{ fontWeight: '500' }}>Start Rent:</span>
                                    <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                        {formatDate(selectedPaymentData.transaction_date)}
                                    </span>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    padding: '8px 0',
                                    borderBottom: '1px solid #e9ecef'
                                }}>
                                    <span style={{ fontWeight: '500' }}>End Rent:</span>
                                    <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                        {formatDate(selectedPaymentData.expired_date)}
                                    </span>
                                </div>
                            </div>
                            {/* Payment Details */}
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                marginBottom: '24px'
                            }}>
                                <h4 style={{ margin: '0 0 16px 0', color: '#333' }}>Payment Details</h4>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    padding: '8px 0',
                                    borderBottom: '1px solid #e9ecef'
                                }}>
                                    <span style={{ fontWeight: '500' }}>Rent Amount:</span>
                                    <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                        {formatCurrency(selectedPaymentData.nominal)}
                                    </span>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    padding: '8px 0',
                                    borderBottom: '1px solid #e9ecef'
                                }}>
                                    <span style={{ fontWeight: '500' }}>Security Deposit:</span>
                                    <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                        {formatCurrency(selectedPaymentData.parentTransaction?.nominal || 0)}
                                    </span>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 0',
                                    background: '#fff',
                                    borderRadius: '6px',
                                    border: '2px solid #1890ff'
                                }}>
                                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Total Amount:</span>
                                    <span style={{
                                        fontWeight: 'bold',
                                        fontSize: '18px',
                                        color: '#1890ff'
                                    }}>
                                        {formatCurrency(
                                            parseFloat(selectedPaymentData.nominal || 0) +
                                            parseFloat(selectedPaymentData.parentTransaction?.nominal || 0)
                                        )}
                                    </span>
                                </div>
                            </div>


                            {/* Action Buttons */}
                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'flex-end'
                            }}>
                                <Button
                                    onClick={() => setPaymentModalVisible(false)}
                                    size="large"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() => handlePayRent(selectedPaymentData.id)}
                                    loading={reviewLoading}
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        border: 'none',
                                        height: '48px',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    💳 Pay Now
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal>

                {/* Photo Modal */}
                <Modal
                    title="Photo View"
                    open={photoModalVisible}
                    onCancel={() => setPhotoModalVisible(false)}
                    footer={null}
                    width={600}
                    centered
                >
                    {selectedPhoto?.map((url, i) => (
                        <div style={{ textAlign: "center" }}>
                            <img
                                src={url}
                                alt="Photo"
                                key={i}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "500px",
                                    objectFit: "contain",
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                                }}
                            />
                        </div>
                    ))}
                </Modal>
            </div >
        </div >
    );
}

export default RentProperty;
